import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { select, Store } from '@ngrx/store';
import moment from 'moment';
import { NGXLogger } from 'ngx-logger';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, Subject, take, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { attendancePercent } from '../../../../models/attendance.school.model';
import {
  GradeLesson,
  GradeLessonStatus,
} from '../../../../models/grade-lesson.school.model';
import { Grade } from '../../../../models/grade.school.model';
import { Group } from '../../../../models/group.model';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { MembershipStatus } from '../../../../models/membership.status';
import { SchoolRoles } from '../../../../models/roles.school.model';
import {
  Session,
  SessionStatus,
} from '../../../../models/session.school.model';
import { FireTimestamp } from '../../../../models/timestamp.firestore.model';
import { UserModel } from '../../../../models/user.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { MomentPipe } from '../../../../pipes/moment.pipe';
import { SchoolAttendanceService } from '../../../../services/attendance.school.service';
import { AuthService } from '../../../../services/auth.service';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { SchoolRolesService } from '../../../../services/school-roles.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import * as memberActions from '../../../../state/actions/member.school.actions';
import * as sessionActions from '../../../../state/actions/session.school.actions';
import * as teacherActions from '../../../../state/actions/teacher.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as gradeSelector from '../../../../state/selectors/grade.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import * as sessionSelector from '../../../../state/selectors/session.school.selector';
import * as teacherSelector from '../../../../state/selectors/teacher.school.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnChipComponent } from '../../../components/buttons/btn-chip/btn-chip.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../../components/buttons/btn-more/btn-more.component';
import { BtnSubmitComponent } from '../../../components/buttons/btn-submit/btn-submit.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ProgressDeterminateComponent } from '../../../components/progress/progress-determinate/progress-determinate.component';
import { ListAssignmentsComponent } from '../../assignments/list-assignments/list-assignments.component';
import { DlgSessionAttendanceComponent } from '../../sessions/dlg-session-attendance/dlg-session-attendance.component';
import { DlgUpsertSessionComponent } from '../../sessions/dlg-upsert-session/dlg-upsert-session.component';

@Component({
  selector: 'app-detail-grade',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnActionComponent,
    BtnChipComponent,
    BtnFabComponent,
    BtnSubmitComponent,
    BtnMoreComponent,
    RouterLink,
    ProgressDeterminateComponent,
    AsyncPipe,
    MomentPipe,
    InitialsPipe,
    ReactiveFormsModule,
    NgxSkeletonLoaderModule,
    ListAssignmentsComponent,
  ],
  templateUrl: './detail-grade.component.html',
  styleUrl: './detail-grade.component.css',
})
export class DetailGradeComponent implements OnInit, OnDestroy {
  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  roles: SchoolRolesService = inject(SchoolRolesService);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  attendanceService: SchoolAttendanceService = inject(SchoolAttendanceService);
  toastr: ToastrService = inject(ToastrService);
  logger: NGXLogger = inject(NGXLogger);
  private dialog: MatDialog = inject(MatDialog);
  authService: AuthService = inject(AuthService);

  searchForm: FormGroup;

  myRoles$: Observable<any> = new Observable();
  lessons$: Observable<GradeLesson[] | null | undefined> = new Observable();
  sessions$: Observable<Session[] | null | undefined> = new Observable();
  faculty$: Observable<SchoolMember[] | null | undefined> = new Observable();
  students$: Observable<SchoolMember[] | null | undefined> = new Observable();

  attendanceMap: Record<string, number> | undefined = undefined;
  allStudents: SchoolMember[] | null | undefined;

  loadingLessons$: Observable<boolean> = new Observable();
  loadingStudents$: Observable<boolean> = new Observable();
  loadingSessions$: Observable<boolean> = new Observable();
  loadingAttendance$: Observable<boolean> = new Observable();

  groupId: string | null = this.route.snapshot.paramMap.get('id');
  gradeId: string | null = this.route.snapshot.paramMap.get('gradeId');

  user: UserModel | null = null;
  group: Group | null | undefined;
  grade: Grade | null | undefined;

  membershipStatus = MembershipStatus;
  memberType = SchoolMemberType;
  lessonStatus = GradeLessonStatus;
  sessionStatus = SessionStatus;
  rolesType = SchoolRoles;

  inviteText: string =
    'Hi, I would like to invite you to join our school group on AtChurch. Please click on the link below to join. https://web.atchurch.app/links/groupinvite?groupId=';

  activeTab: string = 'sessions'; // Default active tab

  showDragDrop: boolean = false;

  exit$ = new Subject<void>();

  constructor(private store: Store<AppState>) {
    this.route.paramMap.subscribe((params) => {
      this.groupId = params.get('id');
      this.gradeId = params.get('gradeId');
    });

    this.searchForm = new FormGroup({
      searchInput: new FormControl('', [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(50),
      ]),
    });

    this.loadingSessions$ = this.store
      .pipe(select(sessionSelector.loading))
      .pipe(takeUntil(this.exit$));
    this.loadingLessons$ = this.store
      .pipe(select(lessonSelector.loading))
      .pipe(takeUntil(this.exit$));
    this.loadingStudents$ = this.store
      .select(teacherSelector.loading)
      .pipe(takeUntil(this.exit$));

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.faculty$ = this.store.select(
      memberSelector.getGroupMembersByType({
        memberType: [SchoolMemberType.teacher],
        groupId: this.groupId,
        status: [MembershipStatus.active],
      }),
    );

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnInit(): void {
    this.store
      .select(groupSelector.getSelectedGroup)
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;

        this.store
          .select(
            gradeSelector.getGradeById({
              gradeId: this.gradeId!,
              groupId: this.groupId!,
              academicYear: this.group?.academicYear ?? null,
            }),
          )
          .pipe(takeUntil(this.exit$))
          .subscribe((grade) => {
            this.grade = grade;
          });

        this.sessions$ = this.store.select(
          sessionSelector.getSessionsByGradeId(
            this.gradeId!,
            this.groupId!,
            this.group?.academicYear,
          ),
        );

        this.lessons$ = this.store.select(
          lessonSelector.getGradeLessonsByGradeId({
            gradeId: this.gradeId!,
            groupId: this.groupId!,
            academicYear: this.group?.academicYear,
            lessonStatus: [GradeLessonStatus.open, GradeLessonStatus.closed],
          }),
        );

        this.students$ = this.store.select(
          teacherSelector.getStudentsByGrade({
            gradeId: this.gradeId!,
            groupId: this.groupId,
            academicYear: this.group?.academicYear ?? null,
          }),
        );

        if (this.group?.academicYear) {
          this.lessons$.pipe(take(1)).subscribe((lessons) => {
            if (lessons?.length === 0) {
              return this.store.dispatch(
                gradeLessonActions.getGradeLessons({
                  groupId: this.groupId!,
                  gradeId: this.gradeId!,
                  academicYear: this.group?.academicYear!,
                  status: [GradeLessonStatus.open, GradeLessonStatus.closed],
                }),
              );
            }
          });
        }
      });

    this.faculty$.pipe(take(1)).subscribe((faculty) => {
      if (faculty?.length === 0) {
        this.store.dispatch(
          memberActions.getMembers({
            source: 'DetailGradeComponent :: ngOnInit : faculty',
            groupId: this.groupId!,
            getMemberType: [SchoolMemberType.teacher],
          }),
        );
      }
    });

    this.students$.pipe(take(1)).subscribe((students) => {
      if (
        students?.length === 0 ||
        (students?.length ?? 0) < (this.group?.gradeStats?.[this.gradeId!] ?? 0)
      ) {
        this.store.dispatch(
          teacherActions.getStudents({
            source: 'DetailGradeComponent :: ngOnInit : student',
            groupId: this.groupId!,
            gradeId: this.gradeId!,
            academicYear: this.group?.academicYear!,
            status: [MembershipStatus.active],
          }),
        );
      }
    });

    this.inviteText = `Hi, I would like to invite you to join our school group '${this.group?.name}' on AtChurch. Please click on the link below to join. https://${environment.domain}/links/groupinvite?groupId=`;
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  async getAttendance() {
    this.loadingAttendance$ = of(true);

    const params = {
      groupId: this.groupId!,
      academicYear: this.group?.academicYear!,
      gradeId: this.grade?.id!,
    };
    try {
      this.logger.debug('DetailGradeComponent :: getAttendance', params);
      const attendance =
        await this.attendanceService.getAttendanceByGradeId(params);
      this.logger.debug(
        'DetailGradeComponent :: getAttendance result',
        attendance?.length ?? 0,
      );

      this.attendanceMap = {};
      for (const a of attendance ?? []) {
        this.attendanceMap[a.studentId] = attendancePercent(a);
      }
    } catch (error) {
      this.logger.error('DetailGradeComponent :: getAttendance failed', error);
    } finally {
      this.loadingAttendance$ = of(false);
    }
  }

  addMoreSessions() {
    this.sessions$.pipe(take(1)).subscribe((sessions) => {
      this.store.dispatch(
        sessionActions.getSessions({
          groupId: this.groupId!,
          academicYear: this.group?.academicYear!,
          gradeId: this.gradeId ?? undefined,
          lastRecord:
            (sessions ?? [])?.length > 0
              ? sessions![sessions!.length - 1]?.startDate
              : undefined,
        }),
      );
    });
  }

  addMoreLessons() {
    this.lessons$.pipe(take(1)).subscribe((lessons) => {
      this.store.dispatch(
        gradeLessonActions.getGradeLessons({
          groupId: this.groupId!,
          academicYear: this.group?.academicYear!,
          status: [GradeLessonStatus.open, GradeLessonStatus.closed],
          gradeId: this.gradeId!,
          lastRecord:
            (lessons ?? [])?.length > 0
              ? lessons![lessons!.length - 1]?.modified
              : undefined,
        }),
      );
    });
  }

  addMoreStudents() {
    this.students$.pipe(take(1)).subscribe((students) => {
      this.store.dispatch(
        teacherActions.getStudents({
          source: 'DetailGradeComponent :: addMoreStudents',
          groupId: this.groupId!,
          gradeId: this.gradeId!,
          academicYear: this.group?.academicYear!,
          status: [MembershipStatus.active],
          lastRecord:
            (students ?? [])?.length > 0
              ? students![students!.length - 1]
              : undefined,
        }),
      );
    });
  }

  onNewLesson() {
    // let dialogConfig = new MatDialogConfig();
    // dialogConfig.data = {
    //   title: 'Create Lesson',
    //   description: `Create a new lesson`,
    //   action: 'Create',
    // };
    // const dialogRef = this.dialog.open(DlgUpsertLessonComponent, dialogConfig);
    // return dialogRef.afterClosed().subscribe((result) => {
    //   if (result && result.lessonName !== undefined && this.groupId) {
    //     const lesson: GradeLesson = {
    //       lessonName: result.lessonName,
    //       groupId: this.groupId,
    //       gradeId: this.gradeId,
    //       createdBy: this.user?.name,
    //       createdById: this.user?.uid,
    //       memberPhotoUrl: this.user?.photoURL ?? '',
    //       academicYear: this.group?.academicYear,
    //     } as GradeLesson;
    //     return this.store.dispatch(
    //       lessonActions.create({ groupId: this.groupId, `${lesson.id}_${}` lesson: lesson })
    //     );
    //   }
    //   return;
    // });
  }

  onDeleteLesson(item: GradeLesson) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Delete Lesson',
      description: `Are you sure you want to delete lesson '${item.lessonName}'?`,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      // showType: 'warning',
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result && item.groupId) {
        return this.store.dispatch(
          gradeLessonActions.deleteAction({
            groupId: item.groupId,
            id: item.id!,
          }),
        );
      }
      return;
    });
  }

  async onNewSession() {
    if (this.grade === null || this.grade === undefined) return;

    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Create Session',
      description: `Create a new session for '${this.grade?.gradeName}' grade`,
      action: 'Create',
      groupId: this.groupId,
      grade: this.grade,
      defaultTeacherId: this.grade?.teacherId,
      faculty$: this.faculty$,
      lessons$: this.lessons$,
      isAssignable: true,
    };

    const dialogRef = this.dialog.open(DlgUpsertSessionComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Session created successfully', '', {
          timeOut: 3000,
        });
      }
    });
  }

  onSessionAttendance(item: Session) {
    if (
      item.id === undefined ||
      !this.grade?.id ||
      !this.groupId ||
      !this.group?.academicYear
    ) {
      this.toastr.error(
        'Required data missing. Kindly login again',
        'Error taking Attendance',
      );
      // return;
    }

    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Update Attendance',
      description: `Update attendance for session '${item.sessionName}'`,
      action: 'Update',
      groupId: this.groupId,
      academicYear: this.group?.academicYear,
      sessionId: item.id,
      sessionStart: item.startDate,
      gradeId: this.grade?.id,
      students$: this.students$,
    };

    const dialogRef = this.dialog.open(
      DlgSessionAttendanceComponent,
      dialogConfig,
    );

    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const session: Session = {
          sessionStatus: SessionStatus.active,
        } as Session;

        return this.store.dispatch(
          sessionActions.update({
            groupId: this.groupId!,
            sessionId: item.id!,
            session: session,
          }),
        );
      }
      return;
    });
  }

  onEditSession(item: Session) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Update Session',
      description: `Update session '${item.sessionName}' details`,
      action: 'Update',
      sessionId: item.id,
      session: item,
      sessionName: item.sessionName,
      startDate: item.startDate.toDate(),
      endDate: item.endDate.toDate(),
      defaultTeacherId: item.teacherId,
      defaultTeacherName: item.teacherName,
      defaultTeacherPhotoUrl: item.teacherPhotoUrl,
      defaultLessonId: item.lessonId,
      defaultLessonName: item.lessonName,
      groupId: this.groupId,
      grade: this.grade,
      faculty$: this.faculty$,
      lessons$: this.lessons$,
      isAssignable: true,
    };

    const dialogRef = this.dialog.open(DlgUpsertSessionComponent, dialogConfig);
    return dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastr.success('Session updated successfully', '', {
          timeOut: 3000,
        });
      }
    });
  }

  onDeleteSession(item: Session) {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.panelClass = ['!p-0', '!bg-transparent', '!shadow-none'];

    dialogConfig.data = {
      title: 'Delete Session',
      description: `Are you sure you want to delete session '${item.sessionName}'?`,
      action: 'Delete',
      buttonColor: 'bg-red-500',
      // showType: 'warning',
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    return dialogRef.afterClosed().subscribe((result) => {
      if (result && item.groupId) {
        return this.store.dispatch(
          sessionActions.deleteAction({ groupId: item.groupId, id: item.id! }),
        );
      }
      return;
    });
  }

  onToggleStatus(item: GradeLesson) {
    this.store.dispatch(
      gradeLessonActions.update({
        groupId: this.group?.id!,
        lessonId: item.id!,
        gradeLesson: {
          ...item,
          lessonStatus:
            item.lessonStatus === GradeLessonStatus.open
              ? GradeLessonStatus.closed
              : GradeLessonStatus.open,
        },
      }),
    );
  }

  isSearching: boolean = false;
  async searchStudents() {
    if (this.searchForm.invalid) return;
    console.log('Searching for students...');

    this.isSearching = true;

    this.allStudents = await this.memberService.searchMembers(
      this.searchForm.get('searchInput')?.value,
      this.groupId!,
      { schoolMemberType: SchoolMemberType.student },
    );

    console.log('Searching complete...');
    this.isSearching = false;
  }

  onRefresh() {
    this.store.dispatch(
      memberActions.getMembers({
        source: 'DetailGradeComponent :: onRefresh',
        groupId: this.groupId!,
        getMemberType: [
          SchoolMemberType.president,
          SchoolMemberType.principal,
          SchoolMemberType.teacher,
        ],
      }),
    );
  }

  setActiveTab(tab: string) {
    if (
      tab === 'overview' &&
      this.activeTab != 'overview' &&
      this.attendanceMap === undefined
    ) {
      this.getAttendance();
    }
    this.activeTab = tab;
    // if (
    //   tab === 'archive' &&
    //   (!this.archivedLessons || this.archivedLessons?.length == 0)
    // ) {
    //   this.addMoreArchiveLessons();
    // }
    this.router.navigate([], {
      fragment: tab,
      queryParamsHandling: 'preserve', // optional, keeps existing query params
      replaceUrl: true, // optional, avoids pushing to browser history
    });
  }
}
